/**
 * NowPlayingPill — small Ableton-style "now playing" indicator in the
 * StudioSidebar. Shows the currently-loaded track name + an animated
 * progress bar during playback. Hides itself when nothing is loaded.
 *
 * iter-180 — uses requestAnimationFrame directly on a ref-mutated DOM
 * node so the progress bar animates at 60fps without re-rendering the
 * React tree.
 *
 * iter-181 — progress bar is now a draggable scrub-and-seek thumb.
 *   pointerdown   → starts dragging (RAF auto-update suspended)
 *   pointermove   → updates the visual position only
 *   pointerup     → calls onSeek(seconds) to actually move the engine
 *   Esc / cancel  → reverts
 *
 * Props:
 *   channels         — engine.channels array (objects with .name, .buffer)
 *   selectedChannel  — currently-selected channel index
 *   playing          — boolean from engine state
 *   ctx              — AudioContext reference for currentTime polling
 *   maxDur           — current session max duration (seconds)
 *   playStartedAt    — engine.startTime
 *   onJumpToChannel  — optional click handler to focus the playing channel
 *   onSeek(seconds)  — seek the engine (iter-181). When provided, the bar
 *                       becomes draggable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Disc3, Pause, Play, Music2, SkipBack, SkipForward } from "lucide-react";

// iter-182 — "M:SS" formatter for the inline playhead readout. Hours are
// rare for a DAW session but supported for safety.
function fmtTime(secs) {
  const s = Math.max(0, Number.isFinite(secs) ? secs : 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
}

export default function NowPlayingPill({
  channels = [],
  selectedChannel = 0,
  playing = false,
  ctx,
  maxDur = 0,
  playStartedAt = 0,
  onJumpToChannel,
  onSeek,
  onTogglePlay,  // iter-183 — inline transport controls
}) {
  const barRef = useRef(null);
  const thumbRef = useRef(null);
  const trackRef = useRef(null);
  const timeRef = useRef(null);  // iter-182 — current playhead time readout
  const lastRafRef = useRef(0);
  const [scrubbing, setScrubbing] = useState(false);
  const scrubbingRef = useRef(false);  // sync access so endScrub can fire same tick
  const scrubPctRef = useRef(0);  // 0..1, what the user dragged to

  // iter-180 — the engine mutates channels in-place (loadBufferToChannel
  // doesn't replace the array reference) AND maxDuration also mutates in
  // place. We poll every 500ms with a cheap tick counter that forces the
  // useMemo below to recompute and re-reads maxDur from the live engine
  // (via a `getMaxDur` callback if provided, else falls back to the prop).
  const [pollTick, setPollTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPollTick((t) => (t + 1) % 1_000_000), 500);
    return () => clearInterval(id);
  }, []);

  // Read fresh maxDur from channels (max of all channel buffer durations)
  // so we never see a stale 0 even if the parent state hasn't synced yet.
  const liveMaxDur = useMemo(() => {
    let m = maxDur || 0;
    for (const c of channels) {
      if (c?.buffer?.duration && c.buffer.duration > m) m = c.buffer.duration;
    }
    return m;
    // pollTick is a deliberate recompute trigger — channels are mutable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, maxDur, pollTick]);

  // Find the "now playing" track.
  const { trackIdx, trackName, totalChannelsWithAudio } = useMemo(() => {
    const withAudio = channels
      .map((c, i) => ({ i, name: c?.name || "", hasAudio: !!c?.buffer }))
      .filter((c) => c.hasAudio);
    const sel = channels[selectedChannel];
    const idx = sel?.buffer ? selectedChannel : (withAudio[0]?.i ?? -1);
    const name = idx >= 0 ? (channels[idx]?.name || `Channel ${String(idx + 1).padStart(2, "0")}`) : "";
    return { trackIdx: idx, trackName: name, totalChannelsWithAudio: withAudio.length };
    // pollTick is a deliberate recompute trigger — channels are mutable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, selectedChannel, pollTick]);

  // Animate the progress bar via RAF — paused while the user is scrubbing.
  // iter-182 fix: when transitioning into a paused/idle state, leave the
  // last bar position + time alone so the user's seek target stays
  // visible after they release. Only the initial mount sets these to 0
  // (via the inline `style={{ width: "0%" }}` on the bar) — after that,
  // RAF only ever moves them FORWARD or to scrub targets.
  useEffect(() => {
    if (scrubbing || !playing || !ctx || !liveMaxDur) {
      return undefined;
    }
    const tick = () => {
      try {
        if (!barRef.current) return;
        const elapsed = Math.max(0, ctx.currentTime - (playStartedAt || 0));
        const pct = Math.min(100, (elapsed / liveMaxDur) * 100);
        barRef.current.style.width = `${pct}%`;
        if (thumbRef.current) thumbRef.current.style.left = `${pct}%`;
        if (timeRef.current) timeRef.current.textContent = fmtTime(Math.min(elapsed, liveMaxDur));
      } catch (e) { void e; }
      lastRafRef.current = requestAnimationFrame(tick);
    };
    lastRafRef.current = requestAnimationFrame(tick);
    return () => { if (lastRafRef.current) cancelAnimationFrame(lastRafRef.current); };
  }, [playing, ctx, liveMaxDur, playStartedAt, scrubbing]);

  // Scrub handlers.
  const updateFromPointer = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
    scrubPctRef.current = pct;
    const pctStr = `${(pct * 100).toFixed(2)}%`;
    if (barRef.current) barRef.current.style.width = pctStr;
    if (thumbRef.current) thumbRef.current.style.left = pctStr;
    // iter-182 — also write the would-be playhead time during scrub so the
    // user sees where they're landing before they release.
    if (timeRef.current && liveMaxDur > 0) {
      timeRef.current.textContent = fmtTime(pct * liveMaxDur);
    }
    return pct;
  }, [liveMaxDur]);

  const startScrub = useCallback((e) => {
    if (!onSeek || !liveMaxDur) return;
    e.preventDefault();
    e.stopPropagation();
    scrubbingRef.current = true;
    setScrubbing(true);
    updateFromPointer(e.clientX);
    // Capture pointer so we keep getting move events even if the cursor
    // leaves the bar.
    try { e.target.setPointerCapture?.(e.pointerId); } catch (err) { void err; }
  }, [onSeek, liveMaxDur, updateFromPointer]);

  const onScrubMove = useCallback((e) => {
    if (!scrubbingRef.current) return;
    updateFromPointer(e.clientX);
  }, [updateFromPointer]);

  const endScrub = useCallback(() => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    setScrubbing(false);
    if (onSeek && liveMaxDur > 0) {
      onSeek(scrubPctRef.current * liveMaxDur);
    }
  }, [onSeek, liveMaxDur]);

  // Don't render if nothing is loaded at all.
  if (totalChannelsWithAudio === 0) return null;

  const cleanedName = (trackName || "").replace(/\.[a-z0-9]+$/i, "").slice(0, 38) || "Untitled";
  const seekable = !!onSeek && liveMaxDur > 0;

  return (
    <div
      data-testid="now-playing-pill"
      className={`group relative w-full overflow-hidden rounded-3xl border bg-gradient-to-b p-4 text-left transition ${
        playing
          ? "border-fuchsia-400/40 from-violet-950/70 to-fuchsia-950/40 shadow-[0_8px_24px_-12px_rgba(244,114,182,0.65)]"
          : "border-white/10 from-zinc-900/60 to-zinc-950/85 hover:border-white/20"
      }`}
    >
      {/* Clickable card body — separated from the scrub area so click ≠ scrub */}
      <button
        type="button"
        data-testid="now-playing-jump-btn"
        onClick={() => onJumpToChannel?.(trackIdx)}
        className="block w-full text-left"
      >
        {/* Header row — disc icon, label, and inline transport controls.
            iter-183 — small SkipBack/Play-Pause/SkipForward buttons on the
            right. Clicks stopPropagation so they don't bubble up to the
            jump-to-channel button below. */}
        <div className="mb-2 flex items-center gap-2">
          <span className="grid h-6 w-6 place-items-center rounded-lg bg-white/[0.06] ring-1 ring-white/10">
            {playing ? (
              <Disc3 className="h-3 w-3 animate-spin text-fuchsia-300" style={{ animationDuration: "2.5s" }} />
            ) : (
              <Music2 className="h-3 w-3 text-white/55" />
            )}
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">
            {playing ? "Now Playing" : "Loaded"}
          </span>
          <div className="ml-auto flex items-center gap-0.5" data-testid="now-playing-mini-transport">
            <span
              role="button"
              tabIndex={0}
              data-testid="now-playing-skip-back"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onSeek && liveMaxDur > 0) onSeek(0); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (onSeek && liveMaxDur > 0) onSeek(0); } }}
              className={`grid h-6 w-6 cursor-pointer place-items-center rounded-md text-white/55 transition hover:bg-white/10 hover:text-white ${(!onSeek || !liveMaxDur) ? "pointer-events-none opacity-30" : ""}`}
              aria-label="Skip to start"
              title="Skip to start"
            >
              <SkipBack className="h-3 w-3" />
            </span>
            <span
              role="button"
              tabIndex={0}
              data-testid="now-playing-play-toggle"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePlay?.(); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTogglePlay?.(); } }}
              className={`grid h-7 w-7 cursor-pointer place-items-center rounded-md transition ${
                playing
                  ? "bg-emerald-500/30 text-emerald-100 ring-1 ring-emerald-400/50 hover:bg-emerald-500/40"
                  : "bg-white/10 text-white ring-1 ring-white/15 hover:bg-white/20"
              } ${!onTogglePlay ? "pointer-events-none opacity-30" : ""}`}
              aria-label={playing ? "Pause" : "Play"}
              title={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-3 w-3 fill-current" /> : <Play className="h-3 w-3 fill-current" />}
            </span>
            <span
              role="button"
              tabIndex={0}
              data-testid="now-playing-skip-forward"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (onSeek && liveMaxDur > 0) onSeek(liveMaxDur); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (onSeek && liveMaxDur > 0) onSeek(liveMaxDur); } }}
              className={`grid h-6 w-6 cursor-pointer place-items-center rounded-md text-white/55 transition hover:bg-white/10 hover:text-white ${(!onSeek || !liveMaxDur) ? "pointer-events-none opacity-30" : ""}`}
              aria-label="Skip to end"
              title="Skip to end"
            >
              <SkipForward className="h-3 w-3" />
            </span>
          </div>
        </div>

        <div
          data-testid="now-playing-title"
          className={`mb-2 line-clamp-2 font-display text-[12px] font-semibold leading-tight ${
            playing ? "text-white" : "text-white/85"
          }`}
          title={trackName}
        >
          {cleanedName}
        </div>

        <div className="mb-2.5 flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-wider text-white/45">
          <span>CH {String((trackIdx >= 0 ? trackIdx : 0) + 1).padStart(2, "0")}</span>
          <span>{totalChannelsWithAudio} track{totalChannelsWithAudio === 1 ? "" : "s"}</span>
        </div>
      </button>

      {/* iter-182 — Time readout above the scrub bar. Shows "M:SS / M:SS"
          format. Left side is the live playhead (updated by RAF), right
          side is the total duration. During scrub the left side previews
          the seek target so the user sees where they're landing. */}
      <div className="mb-1 flex items-baseline justify-between gap-2 font-mono text-[9.5px] tabular-nums">
        <span
          ref={timeRef}
          data-testid="now-playing-current-time"
          className={`${scrubbing ? "text-fuchsia-200" : (playing ? "text-emerald-200/85" : "text-white/55")}`}
        >
          {fmtTime(0)}
        </span>
        <span data-testid="now-playing-total-time" className="text-white/35">
          {fmtTime(liveMaxDur)}
        </span>
      </div>

      {/* iter-181 — Scrub-and-seek progress bar. Clickable / draggable when
          seekable; read-only otherwise. */}
      <div
        ref={trackRef}
        data-testid="now-playing-bar-track"
        onPointerDown={seekable ? startScrub : undefined}
        onPointerMove={seekable ? onScrubMove : undefined}
        onPointerUp={seekable ? endScrub : undefined}
        onPointerCancel={seekable ? endScrub : undefined}
        className={`relative h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06] ${
          seekable ? "cursor-pointer touch-none hover:h-2 transition-[height]" : ""
        }`}
      >
        <div
          ref={barRef}
          data-testid="now-playing-bar"
          className={`pointer-events-none h-full transition-[background-color] duration-300 ${
            playing
              ? "bg-gradient-to-r from-fuchsia-400 via-violet-400 to-cyan-400"
              : "bg-white/15"
          }`}
          style={{ width: "0%" }}
        />
        {seekable && (
          <span
            ref={thumbRef}
            data-testid="now-playing-thumb"
            className={`pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)] transition-[width,height] ${
              scrubbing ? "h-3 w-3" : "h-0 w-0 group-hover:h-2.5 group-hover:w-2.5"
            }`}
            style={{ left: "0%" }}
          />
        )}
      </div>
    </div>
  );
}

